import { useEffect } from 'react';
import { create } from 'zustand';
import { queryClient } from './query';
import { useSession } from './session';

export interface LiveChange { path: string; method: string; actor: string | null; actorId: string | null; id: string | null; at: string }

interface LiveState {
  connected: boolean;
  /** Record ids changed by someone else in the last few seconds → screens flash them. */
  recent: Record<string, number>;
  last: LiveChange | null;
  pulse: number;
}

export const useLive = create<LiveState>(() => ({ connected: false, recent: {}, last: null, pulse: 0 }));

/** True for a few seconds after someone else changed this record. */
export const useRecentlyChanged = (id: string | null | undefined) => useLive((s) => !!id && !!s.recent[id] && Date.now() - s.recent[id]! < 4000);

let refetchTimer: ReturnType<typeof setTimeout> | null = null;

function onChange(e: LiveChange) {
  const me = useSession.getState().boot?.user.id;
  if (e.actorId && e.actorId === me) return; // our own saves already refresh
  useLive.setState((s) => ({ last: e, pulse: s.pulse + 1, recent: e.id ? { ...pruned(s.recent), [e.id]: Date.now() } : s.recent }));
  // Batch bursts of changes into one refresh of what is on screen.
  refetchTimer ??= setTimeout(() => {
    refetchTimer = null;
    void queryClient.invalidateQueries({ refetchType: 'active' });
  }, 600);
}

const pruned = (r: Record<string, number>) => Object.fromEntries(Object.entries(r).filter(([, t]) => Date.now() - t < 10_000));

/** Keeps one server-sent-events connection open while signed in; reconnects with back-off. */
export function useLiveConnection() {
  const token = useSession((s) => s.accessToken);
  useEffect(() => {
    if (!token) return;
    let stop = false;
    let retry = 1000;
    let ctrl: AbortController | null = null;
    const run = async () => {
      while (!stop) {
        ctrl = new AbortController();
        try {
          const res = await fetch('/api/v1/live', { headers: { Authorization: `Bearer ${useSession.getState().accessToken}`, Accept: 'text/event-stream' }, signal: ctrl.signal });
          if (!res.ok || !res.body) throw new Error(String(res.status));
          useLive.setState({ connected: true });
          retry = 1000;
          const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
          let buf = '';
          for (;;) {
            const { value, done } = await reader.read();
            if (done) break;
            buf += value;
            let i: number;
            while ((i = buf.indexOf('\n\n')) >= 0) {
              const block = buf.slice(0, i);
              buf = buf.slice(i + 2);
              const type = /^event: (.*)$/m.exec(block)?.[1];
              const data = /^data: (.*)$/m.exec(block)?.[1];
              if (type === 'change' && data) onChange(JSON.parse(data) as LiveChange);
            }
          }
        } catch {
          /* dropped or token expired: retry */
        }
        useLive.setState({ connected: false });
        if (stop) break;
        await new Promise((r) => setTimeout(r, retry));
        retry = Math.min(retry * 2, 30_000);
      }
    };
    void run();
    return () => {
      stop = true;
      ctrl?.abort();
    };
  }, [token]);
}
